// Everything the app keeps in localStorage on behalf of a signed-in agent goes
// through these keys, so it is scoped to that agent and can be cleaned up.
//
//   sunsure.<agentId>.pref.<name>   preferences (columns, density, page size,
//                                   saved views, recent exports). No client
//                                   records; kept across sign-outs.
//   sunsure.<agentId>.draft.<name>  unsaved form data, which can contain client
//                                   personal details. Deleted on sign-out, when
//                                   a different agent signs in on this browser,
//                                   and after DRAFT_TTL_MS.
//
// The colour-mode setting (ThemeModeProvider) is a device preference and is
// deliberately left out of this scheme.

const PREFIX = "sunsure.";
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export function prefKey(agentId, name) {
  return `${PREFIX}${agentId}.pref.${name}`;
}

export function draftKey(agentId, name) {
  return `${PREFIX}${agentId}.draft.${name}`;
}

function storage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function keysMatching(predicate) {
  const store = storage();
  if (!store) return [];
  const keys = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && predicate(key)) keys.push(key);
  }
  return keys;
}

function remove(key) {
  try {
    storage()?.removeItem(key);
  } catch {
    // Best-effort.
  }
}

/** Returns a draft's saved data, or null if missing, unreadable or expired (expired drafts are deleted). */
export function readDraft(key, now = Date.now()) {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft.savedAt !== "number" || now - draft.savedAt > DRAFT_TTL_MS) {
      remove(key);
      return null;
    }
    return draft;
  } catch {
    remove(key);
    return null;
  }
}

export function writeDraft(key, formData, now = Date.now()) {
  try {
    storage()?.setItem(key, JSON.stringify({ savedAt: now, formData }));
  } catch {
    // Drafts are best-effort.
  }
}

export function removeDraft(key) {
  remove(key);
}

/**
 * Deletes saved drafts. With `exceptAgentId`, that agent's drafts are kept
 * (used at sign-in, so only the agent now using the browser keeps theirs).
 */
export function clearDrafts({ exceptAgentId } = {}) {
  const keep = exceptAgentId != null ? `${PREFIX}${exceptAgentId}.` : null;
  keysMatching(
    (key) => key.startsWith(PREFIX) && key.includes(".draft.") && !(keep && key.startsWith(keep))
  ).forEach(remove);
}
